import React, { useEffect, useState } from 'react';
import { Modal, Button, TextField, IconButton, Chip, FormControlLabel, Switch } from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { Segment } from '../../interfaces/accounting.interface';
import { SegmentService } from '../../services/segment.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    backgroundColor: '#fff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    padding: '32px',
    borderRadius: '8px'
};

interface SegmentFormData {
    code: string;
    name: string;
    active: boolean;
}

const SegmentList: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.SEGMENT_MANAGE);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);
    const [formData, setFormData] = useState<SegmentFormData>({
        code: '',
        name: '',
        active: true
    });

    const fetchSegments = async () => {
        try {
            setLoading(true);
            const response = await SegmentService.getByInstitution();
            setSegments(Array.isArray(response.content) ? response.content : []);
        } catch (err) {
            toast.showApiError(err, 'Failed to load segments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSegments();
    }, []);

    const handleOpenModal = (segment?: Segment) => {
        if (segment) {
            setSelectedSegment(segment);
            setFormData({
                code: segment.code,
                name: segment.name,
                active: segment.active !== false
            });
        } else {
            setSelectedSegment(null);
            setFormData({ code: '', name: '', active: true });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedSegment(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (selectedSegment) {
                await SegmentService.update(selectedSegment.id!, {
                    id: selectedSegment.id,
                    code: formData.code,
                    name: formData.name,
                    active: formData.active
                });
            } else {
                await SegmentService.create({
                    code: formData.code,
                    name: formData.name,
                    active: formData.active
                });
            }
            toast.showSuccess('Segment saved');
            await fetchSegments();
            handleCloseModal();
        } catch (err) {
            toast.showApiError(err, 'Failed to save segment');
        }
    };

    if (loading) {
        return <div style={{ padding: '20px' }}>Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.segmentList.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2>Segments</h2>
                {canManage && (
                    <Button data-help="accounting.segmentList.new" variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpenModal()}>
                        New Segment
                    </Button>
                )}
            </div>

            <div data-help-section="accounting.segmentList.table">
            <ScrollableTable>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                    <tr>
                        <th data-help="accounting.segmentList.col.code" style={tableHeaderStyle}>Code</th>
                        <th data-help="accounting.segmentList.col.name" style={tableHeaderStyle}>Name</th>
                        <th data-help="accounting.segmentList.col.active" style={tableHeaderStyle}>Active</th>
                        {canManage && <th data-help="accounting.segmentList.col.actions" style={tableHeaderStyle}>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {segments.length === 0 ? (
                        <tr>
                            <td style={tableCellStyle} colSpan={canManage ? 4 : 3}>No segments found</td>
                        </tr>
                    ) : (
                        segments.map(segment => (
                            <tr key={segment.id}>
                                <td style={tableCellStyle}>{segment.code}</td>
                                <td style={tableCellStyle}>{segment.name}</td>
                                <td style={tableCellStyle}>
                                    {segment.active !== false ? (
                                        <Chip data-help="accounting.segmentList.active" label="Active" color="success" size="small" />
                                    ) : (
                                        <Chip data-help="accounting.segmentList.active" label="Inactive" size="small" />
                                    )}
                                </td>
                                {canManage && (
                                    <td style={tableCellStyle}>
                                        <IconButton data-help="accounting.segmentList.edit" size="small" onClick={() => handleOpenModal(segment)} color="primary" aria-label="edit">
                                            <Edit />
                                        </IconButton>
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            </ScrollableTable>
            </div>

            <Modal open={isModalOpen} onClose={handleCloseModal}>
                <div data-help-page="accounting.segmentList.form.page" style={modalStyle}>
                    <h2>{selectedSegment ? 'Edit Segment' : 'New Segment'}</h2>
                    <form onSubmit={handleSubmit}>
                        <TextField data-help="accounting.segmentList.form.field.code"
                            fullWidth
                            label="Code"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField data-help="accounting.segmentList.form.field.name"
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            margin="normal"
                        />
                        <FormControlLabel data-help="accounting.segmentList.form.field.active"
                            control={
                                <Switch
                                    checked={formData.active}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                    color="primary"
                                />
                            }
                            label="Active"
                            style={{ marginTop: '8px' }}
                        />
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <Button data-help="accounting.segmentList.form.cancel" onClick={handleCloseModal}>Cancel</Button>
                            <Button data-help="accounting.segmentList.form.save" type="submit" variant="contained" color="primary">
                                {selectedSegment ? 'Update' : 'Create'}
                            </Button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
};

export default SegmentList;
